import React, { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { useReactToPrint } from "react-to-print"
import { toast } from "react-toastify"
import * as XLSX from "xlsx"
import { useSelector } from "react-redux"
import Table from "../../../core/common/dataTable/index"
import TooltipOption from "../../../core/common/tooltipOption"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos"
import {
  BookIssue,
  getAllBookIssues,
} from "../../../service/book-issue"
import { getBookItemByBarcode } from "../../../service/books"
import { RECIPIENT_TYPES } from "../../../constant"
import { all_routes } from "../../router/all_routes"
import IssueBookModal from "./issueBookModal"
import ReturnBookModal from "./ReturnBookModal"
import { User } from "../../../service/student"

// Pagination data type interface
export interface paginationDataType {
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
  limit: number
  total: number
  totalPages: number
}

const IssueBook = () => {
  const routes = all_routes
  const currentBranch = useSelector(selectCurrentBranch)

  const componentRef = useRef<HTMLDivElement>(null)

  const [data, setData] = useState<BookIssue[]>([])
  const [loading, setLoading] = useState(false)
  const [returnIssue, setReturnIssue] = useState<BookIssue | null>(null)
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [searchText, setSearchText] = useState("")
  const [paginationData, setPaginationData] = useState<paginationDataType>()
  const [pagination, setPagination] = useState({ limit: 10, page: 1 })

  // Barcode scanner state
  const [barcodeInput, setBarcodeInput] = useState("")
  const [barcodeResult, setBarcodeResult] = useState<{ item: any; book: any; issue?: any } | null>(null)
  const [barcodeLoading, setBarcodeLoading] = useState(false)
  const [barcodeError, setBarcodeError] = useState("")
  const [showCameraModal, setShowCameraModal] = useState(false)
  const html5QrcodeRef = useRef<any>(null)

  // Let useEffect cleanup handle stopping — avoids double-stop
  const stopCamera = () => setShowCameraModal(false)

  const searchByBarcode = async (value?: string) => {
    const barcode = (value ?? barcodeInput).trim()
    if (!barcode) return
    setBarcodeLoading(true)
    setBarcodeError("")
    setBarcodeResult(null)
    try {
      const res = await getBookItemByBarcode(barcode)
      if (res.success && res.data) {
        setBarcodeResult(res.data)
      } else {
        setBarcodeError("No book found for this barcode.")
      }
    } catch (err: any) {
      setBarcodeError(err?.message || "Barcode not found.")
    } finally {
      setBarcodeLoading(false)
    }
  }

  const startCameraScanner = () => {
    setBarcodeError("")
    setBarcodeResult(null)
    setShowCameraModal(true)
  }

  const safeStopScanner = (s: any) => {
    if (!s) return
    try {
      s.stop().catch(() => {})
    } catch {}
  }

  // All values inside are stable (state setters + module imports) so [showCameraModal] is complete
  useEffect(() => {
    if (!showCameraModal) return
    let scanner: any = null
    const init = async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode")
        scanner = new Html5Qrcode("issue-book-barcode-scanner")
        html5QrcodeRef.current = scanner
        await scanner.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: (w: number, h: number) => ({
              width: Math.min(300, Math.round(w * 0.85)),
              height: Math.min(120, Math.round(h * 0.4)),
            }),
          },
          async (val: string) => {
            safeStopScanner(scanner)
            html5QrcodeRef.current = null
            setBarcodeInput(val)
            setShowCameraModal(false)
            setBarcodeLoading(true)
            setBarcodeError("")
            setBarcodeResult(null)
            try {
              const res = await getBookItemByBarcode(val)
              if (res.success && res.data) {
                setBarcodeResult(res.data)
              } else {
                setBarcodeError("No book found for this barcode.")
              }
            } catch (err: any) {
              setBarcodeError(err?.message || "Barcode not found.")
            } finally {
              setBarcodeLoading(false)
            }
          },
          () => {},
        )
      } catch (err: any) {
        toast.error(err?.message || "Camera access denied. Please allow camera permission.")
        setShowCameraModal(false)
      }
    }
    init()
    return () => {
      safeStopScanner(scanner)
      html5QrcodeRef.current = null
    }
  }, [showCameraModal])

  // Reset to page 1 when search text changes
  useEffect(() => {
    if (searchText !== "") {
      setPagination(prev => ({ ...prev, page: 1 }))
    }
  }, [searchText])

  // Print functionality using useReactToPrint
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: "Book Issues Report",
  })

  const fetchBookIssues = async () => {
    try {
      setLoading(true)
      const response = await getAllBookIssues({
        ...pagination,
        searchText,
      })

      if (response.success) {
        setData(response.data)
        const paginationResponse = response.pagination
        setPaginationData({
          currentPage: paginationResponse.currentPage,
          limit: paginationResponse.limit,
          total: paginationResponse.total,
          totalPages: paginationResponse.totalPages,
          hasNextPage: paginationResponse.hasNextPage,
          hasPrevPage: paginationResponse.hasPrevPage,
        })
      }
    } catch (error) {
      console.error("Failed to fetch book issues:", error)
      toast.error("Failed to fetch book issues")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBookIssues()
  }, [pagination, searchText])

  const handleChange = (page: number, pageSize: number) => {
    setPagination({ limit: pageSize, page: page })
  }

  const handleIssueSuccess = () => {
    fetchBookIssues()
  }

  const handleExportExcel = async () => {
    try {
      const exportData = data

      const worksheet = XLSX.utils.json_to_sheet(
        exportData.map(item => {
          return {
            "Recipient Type": item.recipient_type,
            "Recipient ID": item.recipientIdentifier,
            "Recipient user id": item.recipientUser._id,
            "Book Issued": item.book,
            Barcode: item.bookItemDoc.barcode,
            "Date of Issue": item.issue_date,
            "Due Date": item.due_date,
            Status: item.status,
            "Issue Remarks": item.remarks,
          }
        }),
      )

      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, "Book Issues")
      XLSX.writeFile(workbook, "book_issues.xlsx")
      toast.success("Book issues exported to Excel successfully!")
    } catch (error) {
      toast.error("Error exporting to Excel")
    }
  }

  const handleRowSelection = (selectedRowKeys: React.Key[]) => {
    const stringKeys = selectedRowKeys.map(key => String(key))
    setSelectedRows(stringKeys)
  }
  const handleBulkActions = () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one record")
      return
    }
  }

  const columns = [
    {
      title: "Recipient ID",
      dataIndex: "recipientIdentifier",
      render: (text: string, record: BookIssue) => {
        const isStudent = record.recipient_type === RECIPIENT_TYPES.STUDENT
        const label = text || record.recipientIdentifier || "N/A"
        if (isStudent && record.enrollmentId) {
          return (
            <Link
              to={`/student/student-details/${record.enrollmentId}`}
              className="link-primary"
            >
              {label}
            </Link>
          )
        }
        return <span className="link-primary">{label}</span>
      },
    },
    {
      title: "Recipient",
      dataIndex: "recipientUser",
      render: (data: User, record: BookIssue) => {
        const isStudent = record.recipient_type === RECIPIENT_TYPES.STUDENT
        const nameLink =
          isStudent && record.enrollmentId ? (
            <Link to={`/student/student-details/${record.enrollmentId}`}>
              {`${data.firstName} ${data.lastName}`}
            </Link>
          ) : (
            <Link to="#">{`${data.firstName} ${data.lastName}`}</Link>
          )
        return (
          <div className="d-flex align-items-center">
            <div className="ms-2">
              <p className="text-dark mb-0">{nameLink}</p>
              <span className="fs-12">{record.recipient_type}</span>
            </div>
          </div>
        )
      },
    },
    {
      title: "Book Issued",
      dataIndex: "booksIssued",
      render: (text: string, record: BookIssue) => (
        <span>{text || (record as any).book?.title || "N/A"}</span>
      ),
    },
    {
      title: "Barcode",
      dataIndex: "bookItemBarcode",
      render: (_: any, record: BookIssue) => {
        const r: any = record as any
        return r.bookItemBarcode || "N/A"
      },
    },
    {
      title: "Date of Issue",
      dataIndex: "issue_date",
      render: (text: string) => {
        if (!text) return "N/A"
        try {
          const date = new Date(text)
          return date.toLocaleDateString("en-US", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        } catch {
          return text
        }
      },
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      render: (text: string) => {
        if (!text) return "N/A"
        try {
          const date = new Date(text)
          return date.toLocaleDateString("en-US", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        } catch {
          return text
        }
      },
    },
    {
      title: "Return Date",
      dataIndex: "return_date",
      render: (text: Date, record: BookIssue) => {
        if (!record.return_date) return "—"
        try {
          const date = new Date(record.return_date)
          return date.toLocaleDateString("en-US", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        } catch {
          return "—"
        }
      },
    },
    {
      title: "Issued By",
      dataIndex: "issued_by",
      render: (_: any, record: BookIssue) => {
        if (!record.issued_by) return "N/A"
        return `${record.issued_by.firstName || ""} ${record.issued_by.lastName || ""}`.trim() || "N/A"
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: string, record: BookIssue) => {
        const isReactivated =
          status === "issued" &&
          String(record?.remarks || "")
            .toLowerCase()
            .includes("reactivated")
        const isOverdue =
          status === "issued" &&
          !isReactivated &&
          record?.due_date &&
          new Date(record.due_date) < new Date()
        const displayStatus = isOverdue
          ? "Overdue"
          : isReactivated
            ? "Active"
          : status?.charAt(0).toUpperCase() + status?.slice(1)
        return (
          <span
            className={`badge ${
              status === "returned"
                ? "bg-success"
                : status === "lost"
                  ? "bg-warning"
                : isOverdue
                  ? "bg-danger"
                  : "bg-info"
            }`}
          >
            {displayStatus}
          </span>
        )
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_text: string, record: BookIssue) => {
        const canReturn =
          record.status === "issued" &&
          !String(record?.remarks || "")
            .toLowerCase()
            .includes("reactivated")
        return (
          <div className="d-flex gap-2">
            <button
              className="btn btn-primary btn-sm"
              onClick={() => canReturn && setReturnIssue(record)}
              disabled={!canReturn}
            >
              Return
            </button>
          </div>
        )
      },
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
    },
  ]

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Issue Books</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Management</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Issue Books
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              {/* Bulk actions button like Books component */}
              {selectedRows.length > 0 && (
                <div className="mb-2 me-2">
                  <button
                    className="btn btn-danger"
                    onClick={handleBulkActions}
                  >
                    <i className="ti ti-trash-x me-2" />
                    Bulk Actions ({selectedRows.length})
                  </button>
                </div>
              )}
              {/* TooltipOption with same functionality as Books component */}
              <TooltipOption
                onRefresh={fetchBookIssues}
                onExportPdf={handlePrint}
                onExportExcel={handleExportExcel}
              />
              <button
                className="btn btn-primary me-2 mb-2"
                data-bs-toggle="modal"
                data-bs-target="#issue_book_modal"
              >
                <i className="ti ti-square-rounded-plus-filled me-2" />
                Issue Book
              </button>
            </div>
          </div>

          {/* Barcode Scanner */}
          <div className="card mb-3">
            <div className="card-body py-2">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div className="input-group" style={{ maxWidth: 360 }}>
                  <span className="input-group-text">
                    <i className="ti ti-barcode" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Scan or type barcode..."
                    value={barcodeInput}
                    onChange={e => {
                      setBarcodeInput(e.target.value)
                      setBarcodeResult(null)
                      setBarcodeError("")
                    }}
                    onKeyDown={e => e.key === "Enter" && searchByBarcode()}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={() => searchByBarcode()}
                    disabled={barcodeLoading}
                  >
                    {barcodeLoading ? (
                      <span className="spinner-border spinner-border-sm" />
                    ) : (
                      <i className="ti ti-search" />
                    )}
                  </button>
                </div>
                <button
                  className="btn btn-outline-secondary"
                  title="Scan barcode with camera"
                  onClick={startCameraScanner}
                >
                  <i className="ti ti-scan me-1" />
                  Scan Barcode
                </button>
                {barcodeError && (
                  <span className="text-danger small">{barcodeError}</span>
                )}
              </div>
              {barcodeResult && (
                <div className="mt-3 p-4 border rounded-3 bg-light" style={{ fontSize: 15 }}>
                  <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
                    <div>
                      <h5 className="fw-bold mb-1 text-dark">
                        <i className="ti ti-book me-2 text-primary" />
                        {barcodeResult.book?.title || "Unknown Book"}
                      </h5>
                      <div className="text-muted" style={{ fontSize: 14 }}>by {barcodeResult.book?.author || "—"}</div>
                    </div>
                    <span
                      style={{ fontSize: 14 }} className={`badge px-3 py-2 ${
                        barcodeResult.item?.status === "available"
                          ? "bg-success"
                          : barcodeResult.item?.status === "issued"
                            ? "bg-info"
                            : barcodeResult.item?.status === "lost"
                              ? "bg-danger"
                              : "bg-warning"
                      }`}
                    >
                      {barcodeResult.item?.status
                        ? barcodeResult.item.status.charAt(0).toUpperCase() +
                          barcodeResult.item.status.slice(1)
                        : "Unknown"}
                    </span>
                  </div>
                  <div className="row g-4">
                    <div className="col-6 col-md-3">
                      <div className="text-muted mb-1" style={{ fontSize: 13 }}>Publisher</div>
                      <div className="fw-bold" style={{ fontSize: 15 }}>{barcodeResult.book?.publisher || "—"}</div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="text-muted mb-1" style={{ fontSize: 13 }}>ISBN</div>
                      <div className="fw-bold" style={{ fontSize: 15 }}>{barcodeResult.book?.isbn || "—"}</div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="text-muted mb-1" style={{ fontSize: 13 }}>DDC Class</div>
                      <div className="fw-bold" style={{ fontSize: 15 }}>
                        {barcodeResult.book?.classificationCode
                          ? `${barcodeResult.book.classificationCode}${barcodeResult.book.subClassificationCode ? ` – ${barcodeResult.book.subClassificationCode}` : ""}`
                          : "—"}
                      </div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="text-muted mb-1" style={{ fontSize: 13 }}>Copy ID</div>
                      <div className="fw-bold" style={{ fontSize: 15 }}>{barcodeResult.item?.copyIdentifier || "—"}</div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="text-muted mb-1" style={{ fontSize: 13 }}>Condition</div>
                      <div className="fw-bold text-capitalize" style={{ fontSize: 15 }}>{barcodeResult.item?.condition || "—"}</div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="text-muted mb-1" style={{ fontSize: 13 }}>Cabinet</div>
                      <div className="fw-bold" style={{ fontSize: 15 }}>{barcodeResult.item?.cabinetNo || "—"}</div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="text-muted mb-1" style={{ fontSize: 13 }}>Shelf</div>
                      <div className="fw-bold" style={{ fontSize: 15 }}>{barcodeResult.item?.shelfNo || "—"}</div>
                    </div>
                    {barcodeResult.issue && (
                      <div className="col-12">
                        <div className="text-muted mb-1" style={{ fontSize: 13 }}>Issued To</div>
                        <div className="fw-bold" style={{ fontSize: 15 }}>
                          {barcodeResult.issue.student?.name || "—"}
                          {barcodeResult.issue.student?.admission_no && (
                            <span className="text-muted ms-2 small">({barcodeResult.issue.student.admission_no})</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {barcodeResult.issue && (
                    <div className="mt-3">
                      <button
                        className="btn btn-warning"
                        onClick={() => setReturnIssue(barcodeResult.issue)}
                      >
                        <i className="ti ti-arrow-back-up me-2" />Return This Copy
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Issue Books</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="input-icon-start mb-3 me-2 position-relative">
                  {/* <PredefinedDateRanges /> */}
                </div>
              </div>
            </div>
            <div
              className="card-body p-0 py-3 printable-content"
              ref={componentRef}
            >
              <div className="print-only-header">
                <div className="school-brand">
                  <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                  <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
                </div>
                <div className="report-title">Book Issues</div>
                <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
              </div>
              {loading ? (
                <div className="text-center p-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading book issues...</p>
                </div>
              ) : (
                <Table
                  setSearchText={setSearchText}
                  dataSource={data}
                  columns={columns}
                  loading={loading}
                  pagination={{ ...paginationData, onChange: handleChange }}
                  onSelectionChange={handleRowSelection}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <IssueBookModal onIssueSuccess={handleIssueSuccess} />

      {/* Camera Barcode Scanner Modal */}
      {showCameraModal && (
        <>
          <div
            className="modal fade show d-block"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Scan Barcode</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={stopCamera}
                  />
                </div>
                <div className="modal-body p-2">
                  <div id="issue-book-barcode-scanner" style={{ width: "100%" }} />
                  <p className="text-muted small text-center mt-2">
                    Point camera at the barcode — it will scan automatically
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={stopCamera}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      <ReturnBookModal
        issue={returnIssue}
        onClose={() => setReturnIssue(null)}
        onSuccess={() => { fetchBookIssues(); setBarcodeResult(null); setBarcodeInput("") }}
      />
    </>
  )
}

export default IssueBook
